/**
 * Proyecto: MOGOTAX-SERVER
 * Sistema de Diseño Unificado
 * Autor: Megatrack GPS SAS
 * Fecha de creación: 11 de noviembre 2024
 * Licencia: Propiedad Privada
 */

import { styled, keyframes } from '@mui/material/styles';
import { Box, Typography, IconButton } from '@mui/material';

// Animaciones compartidas
export const floatAnimation = keyframes`
  0%, 100% { 
    transform: translateY(0px);
  }
  50% { 
    transform: translateY(-10px);
  }
`;

export const scanAnimation = keyframes`
  0% { 
    transform: translateX(-100%);
  }
  100% { 
    transform: translateX(200%);
  }
`;

export const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Paleta de colores unificada
export const colors = {
  // Degradado principal de fondo
  backgroundGradient: 'linear-gradient(135deg, #0a2e5c 0%, #051b38 100%)',
  
  // Efectos radiales de fondo
  backgroundRadial: `
    radial-gradient(circle at 20% 80%, rgba(10, 46, 92, 0.4) 0%, transparent 60%),
    radial-gradient(circle at 80% 20%, rgba(10, 46, 92, 0.25) 0%, transparent 60%),
    radial-gradient(circle at 60% 60%, rgba(10, 46, 92, 0.18) 0%, transparent 50%)
  `,
  
  // Glass effect - Estilo Login (más claro)
  glassLight: {
    background: 'rgba(255, 255, 255, 0.95)',
    border: '2px solid rgba(255, 255, 255, 0.3)',
    boxShadow: `
      0 20px 40px rgba(0, 0, 0, 0.3),
      0 0 0 1px rgba(255, 255, 255, 0.2),
      inset 0 1px 0 rgba(255, 255, 255, 0.8),
      0 0 30px rgba(10, 46, 92, 0.2)
    `,
  },
  
  // Glass effect - Estilo Dashboard (más oscuro) 
  glassDark: {
    background: 'rgba(10, 46, 92, 0.1)',
    border: '2px solid rgba(10, 46, 92, 0.3)',
    boxShadow: `
      0 20px 40px rgba(0, 0, 0, 0.4),
      0 0 0 1px rgba(10, 46, 92, 0.2),
      inset 0 1px 0 rgba(10, 46, 92, 0.3),
      0 0 30px rgba(10, 46, 92, 0.4)
    `,
  },
  
  // Colores de texto
  text: {
    primary: '#ffffff',
    secondary: 'rgba(255, 255, 255, 0.9)',
    muted: 'rgba(255, 255, 255, 0.7)',
  },
  
  // Logo drop shadow
  logoShadow: 'drop-shadow(0 0 20px rgba(10, 46, 92, 0.8))',
};

// Contenedor principal unificado
export const UnifiedContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  padding: 0,
  background: colors.backgroundGradient,
  position: 'relative',
  overflow: 'hidden',
  backgroundImage: colors.backgroundRadial,
}));

// Logo unificado
export const UnifiedLogo = styled('img')(({ theme, isMobile }) => ({
  width: isMobile ? 60 : 100,
  marginTop: isMobile ? theme.spacing(2) : theme.spacing(0),
  marginBottom: isMobile ? theme.spacing(2) : theme.spacing(3),
  filter: colors.logoShadow,
  animation: `${floatAnimation} 6s ease-in-out infinite`,
}));

// Card principal unificado - Estilo claro (como login)
export const UnifiedCardLight = styled(Box)(({ theme, isMobile }) => ({
  ...colors.glassLight,
  backdropFilter: 'blur(20px)',
  borderRadius: isMobile ? 0 : 20,
  padding: isMobile ? `${theme.spacing(2)} 0` : theme.spacing(4),
  position: 'relative',
  overflow: 'hidden',
  animation: `${fadeIn} 0.5s cubic-bezier(0.4, 0, 0.2, 1)`,

  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(10, 46, 92, 0.1), transparent)',
    animation: `${scanAnimation} 8s linear infinite`,
    zIndex: 0,
    pointerEvents: 'none',
  },
}));

// Card principal unificado - Estilo oscuro
export const UnifiedCardDark = styled(Box)(({ theme, isMobile }) => ({
  ...colors.glassDark,
  backdropFilter: 'blur(20px)',
  borderRadius: 20,
  padding: isMobile ? theme.spacing(3) : theme.spacing(4),
  position: 'relative',
  overflow: 'hidden',
  animation: `${fadeIn} 0.5s cubic-bezier(0.4, 0, 0.2, 1)`,
  
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(10, 46, 92, 0.2), transparent)',
    animation: `${scanAnimation} 8s linear infinite`,
    zIndex: 0,
    pointerEvents: 'none',
  },
}));

// Título unificado
export const UnifiedTitle = styled(Typography)(({ theme, isMobile }) => ({
  textAlign: 'center',
  marginBottom: isMobile ? theme.spacing(2) : theme.spacing(3),
  color: colors.text.primary,
  fontSize: isMobile ? '1.3rem' : '2rem',
  fontWeight: 700,
  position: 'relative',
  zIndex: 10,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1),
  textShadow: '0 0 10px rgba(255, 255, 255, 0.3)',
}));

// Botón de navegación unificado
export const UnifiedIconButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  backdropFilter: 'blur(10px)',
  border: '2px solid rgba(255, 255, 255, 0.2)',
  color: colors.text.primary,
  marginBottom: theme.spacing(2),
  transition: 'all 0.3s ease',
  position: 'relative',
  zIndex: 10,
  
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    borderColor: 'rgba(255, 255, 255, 0.4)',
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 20px rgba(10, 46, 92, 0.3)',
  },
}));

// Loading container unificado
export const UnifiedLoadingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(6),
  gap: theme.spacing(3),
  ...colors.glassLight,
  backdropFilter: 'blur(20px)',
  borderRadius: 20,
  position: 'relative',
  overflow: 'hidden',
  
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(10, 46, 92, 0.2), transparent)',
    animation: `${scanAnimation} 8s linear infinite`,
    zIndex: 0,
  },
}));

// Layout de contenido centrado
export const CenteredLayout = ({ children, isMobile, maxWidth = 600 }) => (
  <Box sx={{
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: maxWidth,
    width: '100%',
    margin: '0 auto',
    padding: 0,
  }}>
    {children}
  </Box>
);